// src/components/admin/settings/ParamsSettings.jsx
import React, { useState, useEffect } from 'react';
import DataTableSettings from '../../common/DataTableSettings';
import ModalComponent from '../../common/ModalComponent';
import { AlertCircle, DollarSign, Clock, Building } from 'lucide-react';
import { paramsApi } from '../../../api/params-api';
import { toast } from 'react-toastify';

const ParamsSettings = () => {
  const [params, setParams] = useState([]);
  const [organismes, setOrganismes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentParam, setCurrentParam] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    seuil: '',
    numberOfDays: '',
    organismeId: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    // Fetch real data from API
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch params and organismes in parallel
        const [paramsData, organismesData] = await Promise.all([
          paramsApi.getParams(),
          paramsApi.getOrganismesPaiement()
        ]);
        
        // Transform params data to include organism name
        const transformedParams = paramsData.map(param => ({
          ...param,
          organismeName: param.organisme?.nomOrganisme || 'Non défini'
        }));
        
        setParams(transformedParams);
        setOrganismes(organismesData);
      } catch (error) {
        console.error('Error fetching params:', error);
        toast.error('Erreur lors du chargement des paramètres');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const columns = [
    {
      Header: 'Nom du paramètre',
      accessor: 'nom',
      sortable: true,
      searchable: true,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <DollarSign size={16} className="mr-2 text-green-500" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    {
      Header: 'Seuil (DH)',
      accessor: 'seuil',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => (
        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
          {new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'MAD',
            minimumFractionDigits: 2
          }).format(value)}
        </span>
      )
    },
    {
      Header: 'Délai (jours)',
      accessor: 'numberOfDays',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <Clock size={16} className="mr-1 text-blue-500" />
          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
            {value} jours
          </span>
        </div>
      )
    },
    {
      Header: 'Organisme de paiement',
      accessor: 'organismeName',
      sortable: true,
      searchable: true,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <Building size={16} className="mr-1 text-purple-500" />
          <span>{value}</span>
        </div>
      )
    }
  ];

  const handleCreateNew = () => {
    setCurrentParam(null);
    setFormData({
      nom: '',
      seuil: '',
      numberOfDays: '',
      organismeId: ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (param) => {
    setCurrentParam(param);
    setFormData({
      nom: param.nom,
      seuil: param.seuil.toString(),
      numberOfDays: param.numberOfDays.toString(),
      organismeId: param.organismeId
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleDelete = (param) => {
    setCurrentParam(param);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);
      
      // Delete param via API
      await paramsApi.deleteParam(currentParam.id);
      
      // Update local state
      setParams(params.filter(p => p.id !== currentParam.id));
      setIsConfirmDeleteOpen(false);
      
      toast.success('Paramètre supprimé avec succès');
    } catch (error) {
      console.error('Error deleting param:', error);
      toast.error('Erreur lors de la suppression du paramètre');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is filled
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nom) errors.nom = 'Le nom du paramètre est requis';
    if (!formData.seuil) {
      errors.seuil = 'Le seuil est requis';
    } else if (isNaN(parseFloat(formData.seuil)) || parseFloat(formData.seuil) <= 0) {
      errors.seuil = 'Le seuil doit être un nombre positif';
    }
    if (!formData.numberOfDays) {
      errors.numberOfDays = 'Le nombre de jours est requis';
    } else if (isNaN(parseInt(formData.numberOfDays)) || parseInt(formData.numberOfDays) <= 0) {
      errors.numberOfDays = 'Le nombre de jours doit être un nombre entier positif';
    }
    if (!formData.organismeId) errors.organismeId = 'L\'organisme de paiement est requis';
    
    // Check for duplicate parameter name (if creating new)
    if (!currentParam && params.some(p => p.nom.toLowerCase() === formData.nom.toLowerCase())) {
      errors.nom = 'Un paramètre avec ce nom existe déjà';
    }
    
    // Check for duplicate parameter name (if editing)
    if (currentParam && 
        formData.nom.toLowerCase() !== currentParam.nom.toLowerCase() && 
        params.some(p => p.nom.toLowerCase() === formData.nom.toLowerCase())) {
      errors.nom = 'Un paramètre avec ce nom existe déjà';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const paramData = {
        nom: formData.nom,
        seuil: parseFloat(formData.seuil),
        numberOfDays: parseInt(formData.numberOfDays),
        organismeId: formData.organismeId || null
      };
      
      let savedParam;
      
      if (currentParam) {
        // Update existing param
        paramData.id = currentParam.id;
        savedParam = await paramsApi.updateParam(paramData);
        toast.success('Paramètre mis à jour avec succès');
      } else {
        // Create new param
        savedParam = await paramsApi.createParam(paramData);
        toast.success('Paramètre créé avec succès');
      }
      
      // Transform saved param to include organism name for display
      const selectedOrganisme = organismes.find(org => org.id === savedParam.organismeId);
      const transformedParam = {
        ...savedParam,
        organismeName: selectedOrganisme?.nomOrganisme || 'Non défini'
      };
      
      if (currentParam) {
        // Update existing param in local state
        setParams(params.map(p => p.id === currentParam.id ? transformedParam : p));
      } else {
        // Add new param to local state
        setParams([...params, transformedParam]);
      }
      
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving param:', error);
      toast.error('Erreur lors de la sauvegarde du paramètre');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <DataTableSettings
        title="Gestion des Seuils de Paiement"
        subtitle="Configurez les seuils minimum et délais pour les demandes de paiement"
        data={params}
        columns={columns}
        loading={isLoading}
        onCreate={handleCreateNew}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      
      {/* Create/Edit Modal */}
      <ModalComponent
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentParam ? 'Modifier le paramètre' : 'Créer un nouveau paramètre'}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom du paramètre
            </label>
            <input
              type="text"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.nom ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Seuil Standard"
            />
            {formErrors.nom && (
              <p className="mt-1 text-sm text-red-600">{formErrors.nom}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seuil minimum (DH)
              </label>
              <input
                type="number"
                name="seuil"
                value={formData.seuil}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.seuil ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="500.00"
              />
              {formErrors.seuil && (
                <p className="mt-1 text-sm text-red-600">{formErrors.seuil}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Montant minimum requis pour faire une demande de paiement
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Délai de traitement (jours)
              </label>
              <input
                type="number"
                name="numberOfDays"
                value={formData.numberOfDays}
                onChange={handleInputChange}
                min="1"
                step="1"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.numberOfDays ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="30"
              />
              {formErrors.numberOfDays && (
                <p className="mt-1 text-sm text-red-600">{formErrors.numberOfDays}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Nombre de jours pour traiter la demande de paiement
              </p>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organisme de paiement
            </label>
            <select
              name="organismeId"
              value={formData.organismeId}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.organismeId ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner un organisme</option>
              {organismes.map(organisme => (
                <option key={organisme.id} value={organisme.id}>{organisme.nomOrganisme}</option>
              ))}
            </select>
            {formErrors.organismeId && (
              <p className="mt-1 text-sm text-red-600">{formErrors.organismeId}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Organisme responsable du traitement des paiements pour ce seuil
            </p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <DollarSign className="h-5 w-5 text-blue-600 mt-0.5" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-blue-800">
                  Comment ça fonctionne ?
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  Les utilisateurs pourront faire une demande de paiement uniquement s'ils ont atteint 
                  le seuil minimum défini. Le délai indique le temps de traitement estimé pour cette 
                  catégorie de demande.
                </p>
              </div>
            </div>
          </div>
        </div>
      </ModalComponent>
      
      {/* Confirm Delete Modal */}
      <ModalComponent
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        title="Confirmer la suppression"
        onSubmit={confirmDelete}
        isSubmitting={isSubmitting}
        submitLabel="Supprimer"
      >
        <div className="flex items-center space-x-4 text-red-600 mb-4">
          <AlertCircle size={24} />
          <h3 className="text-lg font-medium">Attention</h3>
        </div>
        <p className="text-gray-500">
          Êtes-vous sûr de vouloir supprimer le paramètre <strong>{currentParam?.nom}</strong> ?
        </p>
        <p className="text-gray-500 mt-2">
          Cette action est irréversible. Les utilisateurs utilisant ce paramètre ne pourront plus 
          faire de demandes de paiement selon ces critères.
        </p>
        <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700">
          <p className="font-medium">Paramètres actuels :</p>
          <ul className="list-disc ml-5 mt-1">
            <li>Seuil: {currentParam?.seuil} DH</li>
            <li>Délai: {currentParam?.numberOfDays} jours</li>
            <li>Organisme: {currentParam?.organismeName}</li>
          </ul>
        </div>
      </ModalComponent>
    </div>
  );
};

export default ParamsSettings;